/**
 * @fileoverview The Capabilities page's action buttons.
 * @description "Edit capabilities" while reading; while editing, "Cancel" and "Save changes (n)" —
 * Save is disabled until something has changed (and while the changes are too many for one save),
 * and shows a spinner while the save is in flight.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import ModalFooterButton from '@/shared/components/atoms/ModalFooterButton'

interface CapabilitiesActionsProps {
  editing: boolean
  /** How many cells the draft changes. */
  changeCount: number
  /** True when the changes can't be sent as one save, so Save stays disabled. */
  saveBlocked: boolean
  isSaving: boolean
  onEdit: () => void
  onCancel: () => void
  onSave: () => void
}

/**
 * @component
 * @param {CapabilitiesActionsProps} props - The edit state and the button handlers.
 * @param {boolean} props.editing - Whether the page is in edit mode.
 * @param {number} props.changeCount - Number of unsaved changes.
 * @param {boolean} props.saveBlocked - Whether the changes are too many to save at once.
 * @param {boolean} props.isSaving - Whether a save is in flight.
 * @param {() => void} props.onEdit - Starts editing.
 * @param {() => void} props.onCancel - Abandons the edits.
 * @param {() => void} props.onSave - Saves the changes.
 */
function CapabilitiesActions({
  editing,
  changeCount,
  saveBlocked,
  isSaving,
  onEdit,
  onCancel,
  onSave,
}: CapabilitiesActionsProps) {
  const { t } = useTranslation()

  if (!editing) {
    return (
      <ModalFooterButton
        type="button"
        variant="primary"
        onClick={onEdit}
        data-testid="capabilities-edit-button"
      >
        {t('capabilities.actions.edit')}
      </ModalFooterButton>
    )
  }

  return (
    <>
      <ModalFooterButton type="button" onClick={onCancel} data-testid="capabilities-cancel-button">
        {t('capabilities.actions.cancel')}
      </ModalFooterButton>
      <ModalFooterButton
        type="button"
        variant="primary"
        onClick={onSave}
        isLoading={isSaving}
        disabled={changeCount === 0 || saveBlocked}
        data-testid="capabilities-save-button"
      >
        {changeCount > 0
          ? t('capabilities.actions.saveWithCount', { count: changeCount })
          : t('capabilities.actions.save')}
      </ModalFooterButton>
    </>
  )
}

export default CapabilitiesActions
